import { isDefined } from "@clipboard-health/util-ts";
import StarIcon from "@mui/icons-material/Star";
import { alpha, type Theme } from "@mui/material";
import { capitalizeFirstLetter } from "@src/appV2/lib/utils/capitalizeFirstLetter";
import { A_TEAM_COLOR } from "@src/appV2/Professionals/MyProfessionals/constants";
import { getCredentialsTypeSnippet } from "@src/appV2/WorkerCredentials/BulkShareCredentials/helpers";
import { addHours, differenceInMinutes, isAfter, parseISO } from "date-fns";
import pluralize from "pluralize";
import { createElement } from "react";

export function getWorkerTypeKey(workplaceId: string | undefined): string {
  return `${workplaceId ?? ""}-aTeamSelectedWorkerType`;
}

export function isShiftStartingSoon(
  shiftStartingSoonThresholdInHours: number,
  shiftStartDateTime: string | undefined
): shiftStartDateTime is string {
  return (
    isDefined(shiftStartDateTime) &&
    isAfter(addHours(new Date(), shiftStartingSoonThresholdInHours), parseISO(shiftStartDateTime))
  );
}

export function getStyleForProfessionalCard(props: {
  completedShifts: number;
  isOnATeam: boolean;
  missingCredentials: boolean;
  activeTab: "upcoming" | "past";
  shiftStartDateTime: string | undefined;
  shiftStartingSoonThresholdInHours: number;
  theme: Theme;
  isStarred: boolean;
  isChartingOnly: boolean;
  isTimeClockOnly: boolean;
  isChartingAndTimeClock: boolean;
}) {
  const {
    completedShifts,
    isOnATeam,
    missingCredentials,
    activeTab,
    shiftStartDateTime,
    shiftStartingSoonThresholdInHours,
    theme,
    isStarred,
    isChartingOnly,
    isTimeClockOnly,
    isChartingAndTimeClock,
  } = props;
  const shiftIsStartingSoon = isShiftStartingSoon(
    shiftStartingSoonThresholdInHours,
    shiftStartDateTime
  );
  const credentialsTypeSnippet = getCredentialsTypeSnippet(
    isChartingOnly || isChartingAndTimeClock,
    isTimeClockOnly || isChartingAndTimeClock
  );
  if (
    missingCredentials &&
    activeTab === "upcoming" &&
    shiftIsStartingSoon &&
    completedShifts === 0
  ) {
    const timeUntilShiftStartInHours = Math.ceil(
      differenceInMinutes(parseISO(shiftStartDateTime), new Date()) / 60
    );
    return {
      headerBackgroundColor: alpha(theme.palette.warning.main, 0.7),
      headerText: `New to your workplace \u00A0•\u00A0 ${capitalizeFirstLetter(
        credentialsTypeSnippet
      )} credentials needed in ${timeUntilShiftStartInHours} ${pluralize(
        "hour",
        timeUntilShiftStartInHours
      )}`,
      borderColor: theme.palette.divider,
      cardBackgroundColor: alpha(theme.palette.warning.main, 0.1),
    };
  }

  if (isOnATeam) {
    return {
      headerBackgroundColor: alpha(A_TEAM_COLOR, 0.7),
      headerText: "A-Team Professional",
      borderColor: theme.palette.divider,
      cardBackgroundColor: alpha(A_TEAM_COLOR, 0.1),
    };
  }

  if (isStarred) {
    return {
      headerBackgroundColor: alpha(A_TEAM_COLOR, 0.7),
      headerText: "Starred Professional",
      headerIcon: createElement(StarIcon, { fontSize: "inherit" }),
      borderColor: theme.palette.divider,
      cardBackgroundColor: alpha(A_TEAM_COLOR, 0.1),
    };
  }

  return {
    borderColor: theme.palette.divider,
    cardBackgroundColor: undefined,
    headerBackgroundColor: undefined,
    headerText: undefined,
  };
}
